// Helpers for the GroupMode `get-dashboard-stats` metrics model.
// The API returns one entry per pharmacy; each carries a flat `metrics` array
// whose items are identified by `metricId` (see dashboardMetrics.json catalog).
// Estate-level KPIs (60–64) live on a single company/estate rollup entry; the
// rest are per-branch and shared across the Dashboard, Clinical Checks, Claims,
// Products and Expiring Items tabs.

import catalog from "./dashboardMetrics.json";

export type MetricColor = "green" | "amber" | "red" | string;

export interface DashboardMetric {
  metricId: number;
  metricName: string;
  value: number;
  atRiskValue: number | null;
  color: MetricColor;
  status: string;
}

export interface DashboardPharmacy {
  _id: string;
  companyID: string;
  pharmacyId: string;
  ods: string;
  name: string;
  responsiblePharmacist: string | null;
  pharmacistStatus: string;
  needsAttentionCount: number;
  metrics: DashboardMetric[];
}

export interface MetricCatalogEntry {
  metricId: number;
  metricName: string;
  label: string;
  tab: string;
  operationType: string;
  category: string;
  thresholds: { green: [number, number]; amber: [number, number]; red: [number, number] };
  description: string;
}

// Named metric ids so consuming components never hard-code magic numbers.
export const METRIC = {
  // Per-branch revenue. The "Estate Revenue (MTD)" KPI is the sum across branches.
  REVENUE: 60,
  // Estate rollup metrics — only present on the company/estate entry.
  ACTIVE_BRANCHES: 61,
  TOTAL_STAFF: 62,
  SERVICES_DELIVERED: 63,
  REVENUE_AT_RISK: 64,
  // Per-branch dashboard row
  EPS_AT_RISK: 65,
  EXPIRED_CLAIMS: 66,
  EPS_RETURNED_AFTER: 67,
  EPS_RETURNED_BEFORE: 68,
  PENDING_CD: 69,
  FRIDGE_TEMPS: 70,
  INBOX_CHECK: 71,
  CLAIMS: 72,
  BOOKINGS_TODAY: 73,
  LOGGED_IN_TODAY: 74,
  BRANCH_RAG: 75,
  // Clinical Checks
  CLINICAL_TOTAL: 76,
  CLINICAL_OUTSTANDING: 77,
  CLINICAL_OVERDUE: 78,
  CLINICAL_HIGH_PRIORITY: 79,
  CLINICAL_CHECK_STATUS: 80,
  // Claims
  TOTAL_CLAIMS: 81,
  EXEMPT_CLAIMS: 82,
  NOT_EXEMPT_CLAIMS: 83,
  EVIDENCE_NOT_SEEN: 84,
  CLAIMS_NEEDING_ATTENTION: 85,
  // Products
  PRODUCT_STOCK: 86,
  // Expiring Items
  EXPIRED_ITEMS: 87,
  EXPIRING_ITEMS: 88,
  CRITICAL_EXPIRING: 89,
  EXPIRING_ACTION_NEEDED: 90,
  AT_RISK_VALUE_EXPIRING: 91,
  // Per-category branch RAG (Dashboard branch performance)
  CLINICAL_STATUS: 92,
  SERVICES_STATUS: 93,
  OPERATION_STATUS: 94,
  FINANCE_STATUS: 95,
  COMPLIANCE_STATUS: 96,
  EST_MARGIN: 97,
  // Claims overview columns (get-dashboard-stats)
  MISSING_EXEMPTIONS: 98,
  BETTER_PACK: 99,
  INCOMPLETE_ENDORSEMENTS: 100,
} as const;

export const metricCatalog = catalog as MetricCatalogEntry[];

export const metricCatalogById: Record<number, MetricCatalogEntry> = metricCatalog.reduce(
  (acc, m) => {
    acc[m.metricId] = m;
    return acc;
  },
  {} as Record<number, MetricCatalogEntry>,
);

const resolveMetricId = (idOrName: number | string): number | undefined => {
  if (typeof idOrName === "number") return idOrName;
  const found = metricCatalog.find((m) => m.metricName === idOrName);
  return found?.metricId;
};

/** Look up a single metric on a pharmacy entry by metric id or name. */
export function getMetric(pharmacy: DashboardPharmacy | null | undefined, idOrName: number | string): DashboardMetric | undefined {
  if (!pharmacy?.metrics) return undefined;
  const id = resolveMetricId(idOrName);
  return pharmacy.metrics.find((m) => m.metricId === id);
}

export function getMetricValue(pharmacy: DashboardPharmacy | null | undefined, idOrName: number | string, fallback = 0): number {
  const m = getMetric(pharmacy, idOrName);
  return typeof m?.value === "number" ? m.value : fallback;
}

export function getMetricColor(pharmacy: DashboardPharmacy | null | undefined, idOrName: number | string): MetricColor | undefined {
  return getMetric(pharmacy, idOrName)?.color;
}

/** Every entry is a branch — the payload no longer contains a separate rollup. */
export function getBranchEntries(list: DashboardPharmacy[] | null | undefined): DashboardPharmacy[] {
  return list ?? [];
}

/** Sum a metric's value across a set of entries. */
export function sumMetricValue(list: DashboardPharmacy[], idOrName: number | string): number {
  return list.reduce((s, p) => s + getMetricValue(p, idOrName, 0), 0);
}

/** Sum a metric's atRiskValue across a set of entries. */
export function sumAtRiskValue(list: DashboardPharmacy[], idOrName: number | string): number {
  return list.reduce((s, p) => {
    const v = getMetric(p, idOrName)?.atRiskValue;
    return s + (typeof v === "number" ? v : 0);
  }, 0);
}

export type RagStatus = "healthy" | "at-risk" | "critical";

/** Branch RAG value → status (2 = Healthy, 1 = At Risk, 0 = Critical). */
export function ragValueToStatus(value: number | undefined): RagStatus {
  return value === 2 ? "healthy" : value === 1 ? "at-risk" : "critical";
}

/** Metric colour → RAG status. */
export function colorToStatus(color: MetricColor | undefined): RagStatus {
  return color === "green" ? "healthy" : color === "amber" ? "at-risk" : "critical";
}

/** Tailwind text colour for a metric colour. */
export function colorToTextClass(color: MetricColor | undefined): string {
  return color === "red" ? "text-red-500" : color === "amber" ? "text-amber-500" : color === "green" ? "text-emerald-500" : "text-gray-400";
}

/**
 * Derive a colour/status for an aggregated (e.g. estate-summed) value from the
 * metric's catalog thresholds. Used for KPI cards where the value is computed
 * across branches rather than read from a single metric.
 */
export function statusForValue(metricId: number, value: number): { color: MetricColor; rag: RagStatus; label: string } | undefined {
  const def = metricCatalogById[metricId];
  if (!def) return undefined;
  const inRange = (r: [number, number]) => value >= r[0] && value <= r[1];
  const color: MetricColor = inRange(def.thresholds.green) ? "green" : inRange(def.thresholds.amber) ? "amber" : inRange(def.thresholds.red) ? "red" : "green";
  const rag = colorToStatus(color);
  const label = rag === "healthy" ? "Healthy" : rag === "at-risk" ? "At Risk" : "Critical";
  return { color, rag, label };
}
